(() => {
  const PDFJS_VERSION = "6.2.108";
  const PAGE_FLIP_VERSION = "2.0.7";

  let librariesPromise = null;

  function loadLibraries() {
    if (librariesPromise) {
      return librariesPromise;
    }

    librariesPromise = Promise.all([
      import(
        `https://cdn.jsdelivr.net/npm/page-flip@${PAGE_FLIP_VERSION}/dist/js/page-flip.module.js`
      ),
      import(
        `https://cdn.jsdelivr.net/npm/pdfjs-dist@${PDFJS_VERSION}/build/pdf.min.mjs`
      )
    ]).then(([pageFlipModule, pdfjsLib]) => {
      pdfjsLib.GlobalWorkerOptions.workerSrc =
        `https://cdn.jsdelivr.net/npm/pdfjs-dist@${PDFJS_VERSION}/build/pdf.worker.min.mjs`;

      return {
        PageFlip: pageFlipModule.PageFlip,
        pdfjsLib
      };
    });

    return librariesPromise;
  }

  async function renderPdfPageToImage(pdfPage, targetWidth) {
    const baseViewport = pdfPage.getViewport({
      scale: 1
    });

    const scale = targetWidth / baseViewport.width;

    const viewport = pdfPage.getViewport({
      scale
    });

    const outputScale = Math.min(window.devicePixelRatio || 1, 2);

    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d", {
      alpha: false
    });

    canvas.width = Math.floor(viewport.width * outputScale);
    canvas.height = Math.floor(viewport.height * outputScale);

    const renderContext = {
      canvasContext: context,
      viewport,
      transform: outputScale !== 1
        ? [outputScale, 0, 0, outputScale, 0, 0]
        : null
    };

    await pdfPage.render(renderContext).promise;

    const blob = await new Promise((resolve, reject) => {
      canvas.toBlob(
        (result) => {
          if (result) {
            resolve(result);
          } else {
            reject(new Error("Unable to create catalogue page image."));
          }
        },
        "image/jpeg",
        0.88
      );
    });

    return URL.createObjectURL(blob);
  }

  async function initializeCatalogue(root) {
    if (!root || root.dataset.skrubzInitialized === "true") {
      return;
    }

    root.dataset.skrubzInitialized = "true";

    const pdfUrl = root.dataset.pdfUrl;
    const viewer = root.querySelector("[data-catalogue-viewer]");
    const bookElement = root.querySelector("[data-catalogue-book]");
    const loadingElement = root.querySelector("[data-catalogue-loading]");
    const progressElement = root.querySelector("[data-catalogue-progress]");
    const errorElement = root.querySelector("[data-catalogue-error]");
    const prevButton = root.querySelector("[data-catalogue-prev]");
    const nextButton = root.querySelector("[data-catalogue-next]");
    const pageNumberElement = root.querySelector("[data-catalogue-page]");
    const fullscreenButton = root.querySelector("[data-catalogue-fullscreen]");

    if (!pdfUrl) {
      errorElement.hidden = false;
      errorElement.textContent =
        "Catalogue PDF is not configured. Please add the PDF filename in the Shopify Theme Editor.";
      loadingElement.style.display = "none";
      return;
    }

    let pageFlip = null;
    let objectUrls = [];

    function updatePageIndicator(currentPage) {
      if (!pageFlip) {
        return;
      }

      const totalPages = pageFlip.getPageCount();
      const orientation = pageFlip.getOrientation();

      const firstPage = currentPage + 1;

      let lastPage = firstPage;

      if (orientation === "landscape") {
        lastPage = Math.min(firstPage + 1, totalPages);
      }

      if (firstPage === lastPage) {
        pageNumberElement.textContent =
          `Page ${firstPage} of ${totalPages}`;
      } else {
        pageNumberElement.textContent =
          `Pages ${firstPage}-${lastPage} of ${totalPages}`;
      }

      prevButton.disabled = currentPage <= 0;
      nextButton.disabled = currentPage >= totalPages - 1;
    }

    function showError(error) {
      console.error("SKRUBZ Catalogue Error:", error);

      loadingElement.style.display = "none";
      errorElement.hidden = false;

      errorElement.textContent =
        "We could not load the catalogue. Please refresh the page and try again.";
    }

    try {
      const { PageFlip, pdfjsLib } = await loadLibraries();

      progressElement.textContent = "Opening PDF...";

      const loadingTask = pdfjsLib.getDocument({
        url: pdfUrl
      });

      const pdf = await loadingTask.promise;

      if (!pdf.numPages) {
        throw new Error("The PDF contains no pages.");
      }

      const firstPage = await pdf.getPage(1);

      const baseViewport = firstPage.getViewport({
        scale: 1
      });

      const aspectRatio =
        baseViewport.height / baseViewport.width;

      const isMobile =
        window.matchMedia("(max-width: 749px)").matches;

      const renderWidth = isMobile ? 850 : 1200;

      const basePageWidth = 720;
      const basePageHeight = Math.round(
        basePageWidth * aspectRatio
      );

      progressElement.textContent =
        `Preparing ${pdf.numPages} catalogue pages...`;

      for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber++) {
        const page = await pdf.getPage(pageNumber);

        const imageUrl = await renderPdfPageToImage(
          page,
          renderWidth
        );

        objectUrls.push(imageUrl);

        progressElement.textContent =
          `Preparing page ${pageNumber} of ${pdf.numPages}...`;
      }

      pageFlip = new PageFlip(
        bookElement,
        {
          width: basePageWidth,
          height: basePageHeight,

          size: "stretch",

          minWidth: 280,
          maxWidth: basePageWidth,

          minHeight: Math.round(280 * aspectRatio),
          maxHeight: basePageHeight,

          drawShadow: true,
          flippingTime: 750,

          usePortrait: true,
          showCover: true,

          startPage: 0,

          autoSize: true,

          maxShadowOpacity: 0.35,

          mobileScrollSupport: true,

          swipeDistance: 30,

          clickEventForward: true,

          disableFlipByClick: false
        }
      );

      pageFlip.on("init", (event) => {
        updatePageIndicator(event.data);
      });

      pageFlip.on("flip", (event) => {
        updatePageIndicator(event.data);
      });

      pageFlip.on("changeOrientation", () => {
        updatePageIndicator(
          pageFlip.getCurrentPageIndex()
        );
      });

      pageFlip.loadFromImages(objectUrls);

      loadingElement.style.display = "none";

      prevButton.addEventListener("click", () => {
        if (pageFlip) {
          pageFlip.flipPrev("bottom");
        }
      });

      nextButton.addEventListener("click", () => {
        if (pageFlip) {
          pageFlip.flipNext("bottom");
        }
      });

      fullscreenButton.addEventListener("click", async () => {
        try {
          if (!document.fullscreenElement) {
            await viewer.requestFullscreen();
            fullscreenButton.textContent = "Exit Fullscreen";
          } else {
            await document.exitFullscreen();
            fullscreenButton.textContent = "Fullscreen";
          }
        } catch (error) {
          console.warn(
            "Fullscreen mode is not available.",
            error
          );
        }
      });

      document.addEventListener(
        "fullscreenchange",
        () => {
          if (!document.fullscreenElement) {
            fullscreenButton.textContent = "Fullscreen";
          }
        }
      );

      root.__skrubzCatalogueCleanup = () => {
        if (pageFlip) {
          try {
            pageFlip.destroy();
          } catch (error) {
            console.warn(
              "Catalogue cleanup warning:",
              error
            );
          }
        }

        objectUrls.forEach((url) => {
          URL.revokeObjectURL(url);
        });
      };

    } catch (error) {
      showError(error);

      objectUrls.forEach((url) => {
        URL.revokeObjectURL(url);
      });
    }
  }

  function initializeAllCatalogues(scope = document) {
    const catalogues = scope.querySelectorAll(
      "[data-skrubz-catalogue]"
    );

    catalogues.forEach(initializeCatalogue);
  }

  function cleanupCatalogue(scope) {
    const root =
      scope.matches &&
      scope.matches("[data-skrubz-catalogue]")
        ? scope
        : scope.querySelector?.("[data-skrubz-catalogue]");

    if (root && root.__skrubzCatalogueCleanup) {
      root.__skrubzCatalogueCleanup();
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener(
      "DOMContentLoaded",
      () => initializeAllCatalogues()
    );
  } else {
    initializeAllCatalogues();
  }

  document.addEventListener(
    "shopify:section:load",
    (event) => {
      initializeAllCatalogues(event.target);
    }
  );

  document.addEventListener(
    "shopify:section:unload",
    (event) => {
      cleanupCatalogue(event.target);
    }
  );
})();